// /routes — app-style list of saved field-visit routes.
import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { FaRoute, FaPlus, FaSpinner, FaCalendarDay, FaMapMarkerAlt, FaChevronRight } from 'react-icons/fa';
import DashboardLayout from '../../../layouts/DashboardLayout';
import ModalNotification from '../../../components/shared/ModalNotification';
import RouteFormModal from '../components/RouteFormModal';
import { getRoutes, createRoute } from '../routeService';
import { formatRouteDate, routeDateRelative } from '../utils';

const Page = styled.div`max-width: 1000px; margin: 0 auto;`;
const Header = styled.div`
  display: flex; align-items: center; gap: 12px; margin-bottom: ${p => p.theme.spacing(3)};
  h1 { color: ${p => p.theme.colors.primary}; margin: 0; font-size: 1.45rem; display: flex; align-items: center; gap: 10px; flex: 1; }
`;
const NewBtn = styled.button`
  display: inline-flex; align-items: center; gap: 8px; flex-shrink: 0; padding: 11px 18px; border: none;
  border-radius: 999px; background: ${p => p.theme.colors.primary}; color: #fff; font-weight: 700; font-size: 0.9rem;
  cursor: pointer; box-shadow: 0 4px 14px ${p => p.theme.colors.primary}55;
  &:hover { opacity: 0.92; }
  .lbl { @media (max-width: 480px) { display: none; } }
`;
const Grid = styled.div`display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: ${p => p.theme.spacing(2)};`;
const Card = styled.div`
  background: ${p => p.theme.colors.surface}; border-radius: 16px; border: 1px solid ${p => p.theme.colors.inputGray}22;
  border-left: 4px solid ${p => p.$soon ? p.theme.colors.primary : p.theme.colors.inputGray};
  padding: 16px; cursor: pointer; transition: transform .15s ease, box-shadow .15s ease;
  &:hover { transform: translateY(-2px); box-shadow: 0 8px 22px rgba(0,0,0,0.28); }
  .top { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
  .name { color: ${p => p.theme.colors.textPrimary}; font-weight: 700; font-size: 1.05rem; }
  .date { display: inline-flex; align-items: center; gap: 6px; margin-top: 8px; font-size: 0.85rem; font-weight: 600;
    color: ${p => p.$soon ? p.theme.colors.primary : p.theme.colors.textSecondary}; }
  .meta { display: flex; align-items: center; gap: 6px; margin-top: 10px; color: ${p => p.theme.colors.textSecondary}; font-size: 0.82rem; }
`;
const Loader = styled.div`display: flex; justify-content: center; padding: 90px; color: ${p => p.theme.colors.primary}; svg { font-size: 32px; animation: spin 1s linear infinite; } @keyframes spin { to { transform: rotate(360deg);} }`;
const Empty = styled.div`
  text-align: center; padding: 70px 20px; color: ${p => p.theme.colors.textSecondary};
  .ico { font-size: 2.4rem; color: ${p => p.theme.colors.inputGray}; margin-bottom: 14px; }
  .t { font-size: 1.05rem; color: ${p => p.theme.colors.textPrimary}; font-weight: 600; margin-bottom: 6px; }
`;

const RoutesListPage = () => {
  const navigate = useNavigate();
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [feedback, setFeedback] = useState({ show: false, message: '', success: true });

  const load = useCallback(async () => {
    setLoading(true);
    try { setRoutes(await getRoutes()); }
    catch { setFeedback({ show: true, message: 'Failed to load routes', success: false }); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async ({ name, date }) => {
    const route = await createRoute({ name, date });
    setFormOpen(false);
    navigate(`/routes/${route.id}`);
  };

  return (
    <DashboardLayout>
      <Page>
        <Header>
          <h1><FaRoute /> Routes</h1>
          <NewBtn onClick={() => setFormOpen(true)}><FaPlus /> <span className="lbl">New Route</span></NewBtn>
        </Header>

        {loading ? (
          <Loader><FaSpinner /></Loader>
        ) : routes.length === 0 ? (
          <Empty>
            <div className="ico"><FaRoute /></div>
            <div className="t">No routes yet</div>
            <div>Create a route for a visit day and add the leads you'll see.</div>
          </Empty>
        ) : (
          <Grid>
            {routes.map(r => {
              const soon = ['Today', 'Tomorrow'].includes(routeDateRelative(r.date));
              return (
                <Card key={r.id} $soon={soon} onClick={() => navigate(`/routes/${r.id}`)}>
                  <div className="top">
                    <span className="name">{r.name}</span>
                    <FaChevronRight color="#787878" />
                  </div>
                  <div className="date"><FaCalendarDay size={12} /> {r.date ? `${routeDateRelative(r.date)} · ${formatRouteDate(r.date)}` : 'No date set'}</div>
                  <div className="meta"><FaMapMarkerAlt size={11} /> {r.leadCount} {r.leadCount === 1 ? 'stop' : 'stops'}{r.last_generated_at ? ' · route generated' : ''}</div>
                </Card>
              );
            })}
          </Grid>
        )}
      </Page>

      <RouteFormModal isOpen={formOpen} onClose={() => setFormOpen(false)} onSave={handleCreate} />

      <ModalNotification
        isOpen={feedback.show}
        message={feedback.message}
        success={feedback.success}
        onComplete={() => setFeedback(prev => ({ ...prev, show: false }))}
      />
    </DashboardLayout>
  );
};

export default RoutesListPage;
